import { useState } from "react";
import { toast } from "sonner";
import { Send } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { PageHero } from "./AboutPage";
import { Reveal } from "../components/Reveal";
import { useLang } from "../context/LanguageContext";
import { CATEGORY_KEYS } from "../i18n/translations";
import { api } from "../lib/api";

const empty = { name: "", phone: "", email: "", category: "healthcare", experience_years: "1", gulf_experience: "no", city: "", notes: "" };

export default function ApplyPage() {
  const { t } = useLang();
  const [form, setForm] = useState({ ...empty });
  const [sending, setSending] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/applications", { ...form, category: t(`categories.${form.category}`) });
      toast.success(t("apply.success"));
      setForm({ ...empty });
    } catch {
      toast.error(t("apply.error"));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-[#FAF7F2] min-h-screen" data-testid="apply-page">
      <Header />
      <PageHero overline={t("apply.overline")} title={t("apply.title")} sub={t("apply.sub")} testid="apply-title" img="/assets/hero-2.jpg" />
      <section className="pb-28 px-6 sm:px-12">
        <Reveal>
          <form onSubmit={submit} className="max-w-3xl mx-auto rounded-3xl border border-[#1B2A4E]/10 bg-white p-7 sm:p-10 shadow-sm space-y-5" data-testid="apply-form">
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="label">{t("apply.name")} *</label>
                <input data-testid="apply-name" required className="field" value={form.name} onChange={(e) => set("name", e.target.value)} />
              </div>
              <div>
                <label className="label">{t("apply.phone")} *</label>
                <input data-testid="apply-phone" required className="field" value={form.phone} onChange={(e) => set("phone", e.target.value)} />
              </div>
              <div>
                <label className="label">{t("apply.email")}</label>
                <input data-testid="apply-email" type="email" className="field" value={form.email} onChange={(e) => set("email", e.target.value)} />
              </div>
              <div>
                <label className="label">{t("apply.category")}</label>
                <select data-testid="apply-category" className="field" value={form.category} onChange={(e) => set("category", e.target.value)}>
                  {CATEGORY_KEYS.filter((k) => k !== "other").map((k) => (
                    <option key={k} value={k}>{t(`categories.${k}`)}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label">{t("apply.experience")}</label>
                <select data-testid="apply-experience" className="field" value={form.experience_years} onChange={(e) => set("experience_years", e.target.value)}>
                  {Array.from({ length: 20 }, (_, i) => i + 1).map((y) => (
                    <option key={y} value={y}>{y} {t("form.years")}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label">{t("apply.gulf")}</label>
                <div className="flex gap-2" data-testid="apply-gulf-group">
                  {["yes", "no"].map((v) => (
                    <button
                      type="button"
                      key={v}
                      data-testid={`apply-gulf-${v}`}
                      onClick={() => set("gulf_experience", v)}
                      className={`flex-1 py-2.5 rounded-xl text-xs font-bold border transition-all ${
                        form.gulf_experience === v ? "bg-[#1B2A4E] border-[#1B2A4E] text-white" : "border-[#1B2A4E]/20 text-[#1B2A4E]/60 hover:border-[#E8842C]"
                      }`}
                    >
                      {t(`form.${v}`)}
                    </button>
                  ))}
                </div>
              </div>
              <div className="sm:col-span-2">
                <label className="label">{t("apply.city")}</label>
                <input data-testid="apply-city" className="field" value={form.city} onChange={(e) => set("city", e.target.value)} />
              </div>
              <div className="sm:col-span-2">
                <label className="label">{t("apply.notes")}</label>
                <textarea data-testid="apply-notes" rows={4} className="field" value={form.notes} onChange={(e) => set("notes", e.target.value)} />
              </div>
            </div>
            <button type="submit" data-testid="apply-submit-btn" disabled={sending} className="btn-primary w-full justify-center !py-4 disabled:opacity-50">
              {t("apply.submit")} <Send className="w-4 h-4" />
            </button>
          </form>
        </Reveal>
      </section>
      <Footer />
    </div>
  );
}
